// REGISTRY — full data sheet for the current player. Aggregates data from
// every subsystem (profile, kingdom, plots, buildings, goons, marriages,
// bounties) into a single shareable record. Kingdom Club members can also
// see other players' full Registry sheets when they click on a rank-list
// entry; non-members see only a stripped public summary.

import { Events, on, emit } from './eventBus.js'
import {
  KingdomConfig,
  PlotConfig,
  PlotTypes,
  BuildingConfig,
  GoonConfig,
  GoonTypes,
  TitleConfig,
  ClassConfig,
  LicenseConfig,
  DegreeSubjectLabels,
  DegreeTiers,
  AchievementConfig,
  AchievementRarity,
} from '../config/balance.js'
import { getProfile, active, activeId } from '../game/state.js'
import { getWealth, getGoonsSlain, getBountyTotal, getGoonsOwned, getTaxRate } from '../game/seasonManager.js'
import { getNetWorth, getRealEstateValue } from '../game/netWorth.js'
import { getHonorDisplay } from '../game/honor.js'
import { canSeeFullRegistry } from '../game/membershipManager.js'
import {
  activeMarriages, combinedWealth, isOpenToMarriage, canPropose as marriageCanPropose,
  propose as marriagePropose, divorce as marriageDivorce,
  toggleOpenToMarriage as marriageToggleOpen,
  haveChild as marriageHaveChild, birthBlockReason,
} from '../game/marriageManager.js'
import { progressFor } from '../game/achievementManager.js'
import { getAccountInfo, fetchPublicRegistry, getStreak } from '../game/accountManager.js'
import { getInboxUnread, getInvites, proposalActions } from '../game/inviteManager.js'
import { setActivePage } from './siteRouter.js'
import { openProposeDialog } from './proposeDialog.js'
import { openHouseholdReport } from './householdReport.js'
import { ord } from './proposalPopup.js'
import { formatCoins } from './format.js'
import { pushLog } from './hud.js'
import { makeToggleable } from './railPanel.js'

export function mountRegistryPanel({ root, toggleBtn, dotHost }) {
  root.classList.add('hud__build')
  root.innerHTML = shell()
  const view = root.querySelector('[data-registry-view]')
  let pendingRefresh = false
  // When null we render the player's own Registry. When set, we render the
  // stripped (FREE) or full (Kingdom Club) public profile of another player.
  let viewingTarget = null

  const refresh = () => {
    if (!root.classList.contains('open')) {
      pendingRefresh = true
      return
    }
    const scrollEl = view.querySelector('.reg__scroll')
    const prevScroll = scrollEl ? scrollEl.scrollTop : 0
    if (viewingTarget) renderOther(view, viewingTarget)
    else render(view, 'self')
    const newScroll = view.querySelector('.reg__scroll')
    if (newScroll) newScroll.scrollTop = prevScroll
  }
  refresh()
  // Optional rail toggle button (the registry is normally opened by clicking the
  // profile avatar, which emits RegistryOpen — see kingdomTabs).
  if (toggleBtn) {
    makeToggleable(root, toggleBtn)
    toggleBtn.addEventListener('click', () => {
      viewingTarget = null
      pendingRefresh = false
      if (root.classList.contains('open')) {
        refresh()
      }
    })
  }

  // Paint / clear the unread-marriage-proposals red dot on the profile avatar
  // (dotHost) — falls back to the rail button if one was provided.
  const dotEl = dotHost || toggleBtn
  if (dotEl) {
    installInboxDot(dotEl)
    on(Events.InboxUnreadChange, ({ unread }) => paintInboxDot(dotEl, unread))
  }

  // Account change (login / logout / membership) → repaint with the new account's marriage state.
  on(Events.AccountReady, () => refresh())

  // Cross-panel hook: rank-list rows in kingdomViewPanel emit RegistryOpen
  // with a target → switch into "viewing someone else" mode and pop open.
  // Real registered targets (target.userId set) trigger a server fetch so
  // the public sheet shows live counters/achievements/etc. NPC roster cards
  // (no userId) just render whatever was embedded in the row.
  on(Events.RegistryOpen, ({ target }) => {
    viewingTarget = target ?? null
    root.classList.add('open')
    document.querySelectorAll('[data-rail-panel]').forEach((p) => {
      if (p !== root) p.classList.remove('open')
    })
    // No target → open the player's OWN registry (e.g. the top-bar profile chip).
    if (!viewingTarget) {
      render(view, 'self')
      return
    }
    renderOther(view, target)
    if (target?.userId) {
      fetchPublicRegistry(target.userId).then((rich) => {
        // Re-render only if the viewer hasn't navigated away in the meantime.
        if (viewingTarget === target) {
          viewingTarget = { ...target, ...rich }
          renderOther(view, viewingTarget)
        }
      }).catch((err) => console.warn('[registry] public-fetch failed:', err.message))
    }
  })

  // Wire actions that the rendered HTML exposes via data-attrs. We call the
  // marriage manager directly (rather than via the event bus) so the buttons
  // stay reliable even if a dev-mode HMR reload splits bus instances.
  root.addEventListener('click', (e) => {
    const t = e.target.closest('[data-action]')
    if (!t) return
    const action = t.dataset.action
    if (action === 'toggle-open') {
      marriageToggleOpen()
    } else if (action === 'share-location') {
      shareLocation()
    } else if (action === 'propose-mock') {
      const target = {
        id: `npc_${Date.now()}`,
        name: 'Lady Wu of the Eastern Court',
        kingdom: 'WU',
        wealth: 25_000,
        isNpc: true,
      }
      const ok = marriagePropose(target)
      pushLog(ok
        ? `Marriage proposal sent to ${target.name}. Awaiting reply…`
        : 'Cannot propose — you are at the maximum, or locked as an acceptor.')
    } else if (action === 'divorce') {
      confirmDivorce(t.dataset.mid)
    } else if (action === 'have-child') {
      marriageHaveChild(t.dataset.mid)
    } else if (action === 'open-settings') {
      root.classList.remove('open') // close the registry, open settings on top
      viewingTarget = null
      emit(Events.OpenSettings)
    } else if (action === 'open-cloud') {
      root.classList.remove('open') // close the registry, open cloud sync on top
      viewingTarget = null
      emit(Events.OpenCloud)
    } else if (action === 'print') {
      window.print()
    } else if (action === 'share') {
      shareRegistry()
    } else if (action === 'close') {
      root.classList.remove('open')
      viewingTarget = null
    } else if (action === 'back-to-self') {
      viewingTarget = null
      render(view, 'self')
    } else if (action === 'propose-target') {
      if (!viewingTarget) return
      if (viewingTarget.userId) { // a real player: the real proposal (the server checks genders, the License, 5 wives / 1 husband)
        openProposeDialog({ id: viewingTarget.userId, name: viewingTarget.name, onDone: (msg) => pushLog(msg) })
        return
      }
      const ok = marriagePropose(viewingTarget)
      pushLog(ok
        ? `Marriage proposal sent to ${viewingTarget.name}. Awaiting reply…`
        : 'Cannot propose — you are at the maximum, or locked as an acceptor.')
      viewingTarget = null
      render(view, 'self')
    } else if (action === 'wed-accept' || action === 'wed-decline') {
      liveMarriageAct(action === 'wed-accept' ? () => proposalActions.accept(t.dataset.id) : () => proposalActions.decline(t.dataset.id), action === 'wed-accept' ? 'You are married.' : 'Proposal declined.')
    } else if (action === 'wed-end') {
      if (confirm(`End your marriage to ${t.dataset.name}? Nothing is divided or moved — you simply stop being married, and the Registry shows both of you as Divorced.`)) liveMarriageAct(() => proposalActions.divorce(t.dataset.id), 'The marriage has ended.')
    } else if (action === 'wed-report') {
      openHouseholdReport()
    } else if (action === 'open-registry') {
      root.classList.remove('open'); viewingTarget = null
      setActivePage('registry')
    }
  })

  // Click on the modal backdrop (outside the inner sheet) → close.
  root.addEventListener('click', (e) => {
    if (e.target === root) root.classList.remove('open')
  })

  // Repaint on any signal that affects what the sheet shows.
  for (const evt of [
    Events.WealthChange, Events.GoonOwnedChange, Events.PlotChanged, Events.BuildingAdded, Events.InvitesChange,
    Events.ProfileChange, Events.MembershipChange, Events.OpenToMarriageChange,
    Events.MarriageAccepted, Events.MarriageDivorced, Events.MarriageProposalSent,
    Events.KingdomChange, Events.ChildBorn, Events.TaxRateChange, Events.CitizenPromoted,
    Events.DegreePurchased, Events.QuizFinished,
  ]) on(evt, refresh)
}

function shell() {
  return `
    <span class="hud__build-title">REGISTRY 户籍</span>
    <div data-registry-view></div>
  `
}

// Public profile of another player. FREE viewers see a stripped summary
// with "🔒 Kingdom Club" placeholders over the locked sections; members
// see the full Registry shape (filled with whatever data the target carries
// — until a real backend lands, NPC targets only carry name + kingdom +
// wealth + title, so most rows still show "—"). A "Propose Marriage" CTA
// shows when the target is openToMarriage and the viewer canPropose.
function renderOther(view, target) {
  const kid = target?.kingdom
  const kingdom = KingdomConfig[kid]
  const full = canSeeFullRegistry()
  // Build a deep-view row. FREE viewers always see the 🔒 lock. KC viewers
  // see the real value if the server provided it, or a dim em-dash placeholder
  // while the public-fetch is still in flight.
  const lockedRow = (label, value) => {
    if (!full) return `<div class="reg__row small dim"><span>${label}</span><strong>🔒 Private</strong></div>`
    const v = value == null || value === '' ? '—' : value
    return `<div class="reg__row small"><span>${label}</span><strong>${v}</strong></div>`
  }
  const licenseCount = (target.licenses ?? []).length
  const degreeCount = Object.keys(target.degrees ?? {}).length
  const activeM = (target.marriages ?? []).filter((m) => m.status === 'ACTIVE')
  const pastM = (target.marriages ?? []).filter((m) => m.status === 'DIVORCED')
  const canMarry = target.openToMarriage !== false && (target.userId ? true : marriageCanPropose())

  view.innerHTML = `
    <button class="reg__close" data-action="close" aria-label="Close registry">×</button>
    <div class="reg__scroll styled-scroll">
    <div class="reg__sheet">
      <div class="reg__header">
        <div class="reg__name">${escapeHtml(target.name || '—')}</div>
        <div class="reg__sub">${escapeHtml(target.title || '—')} · ${kingdom?.name ?? kid ?? '—'}</div>
        <div class="reg__sub small">
          Public Registry · ${full ? 'Full deep-view' : 'Public summary'}
        </div>
        <div class="reg__header-actions">
          <button class="reg__icon-btn" data-action="back-to-self" title="Back to my Registry" aria-label="Back">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
          </button>
        </div>
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.wealth} WEALTH</div>
        <div class="reg__row"><span>Cash on hand</span><strong>${formatCoins(target.wealth ?? 0)}</strong></div>
        ${lockedRow('Bounties earned', target.bountyTotal != null ? formatCoins(target.bountyTotal) : null)}
        ${lockedRow('Rival goons slain', target.goonsSlain?.toLocaleString())}
        ${lockedRow('Lifetime clicks', target.clickCount?.toLocaleString())}
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.land} LAND &amp; BUILDINGS</div>
        ${lockedRow('Plots owned', target.plotsCount?.toLocaleString())}
        ${lockedRow('Buildings', target.buildingsCount?.toLocaleString())}
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.education} EDUCATION &amp; LICENSES</div>
        ${lockedRow('Degrees', degreeCount > 0 ? degreeCount : null)}
        ${lockedRow('Licenses', licenseCount > 0 ? licenseCount : null)}
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.marriage} MARRIAGE</div>
        <div class="reg__row">
          <span>Status</span>
          <strong>${target.openToMarriage !== false
            ? '<em>Open to marriage</em>'
            : 'Not accepting proposals'}</strong>
        </div>
        ${lockedRow('Active marriages', target.marriages ? activeM.length : null)}
        ${lockedRow('Divorce record', target.marriages ? pastM.length : null)}
        ${canMarry
          ? `<div class="reg__actions"><button data-action="propose-target">Propose Marriage to ${escapeHtml(target.name)}</button></div>`
          : (target.openToMarriage === false
              ? `<div class="reg__row small dim">This citizen is not currently open to marriage.</div>`
              : `<div class="reg__row small dim">You can't propose — at the maximum already, or locked as an acceptor.</div>`)
        }
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.children} CHILDREN</div>
        ${lockedRow('Number of kittens', target.childrenCount?.toLocaleString())}
      </div>

      ${full && target.counters ? renderPublicStatsSection(target.counters, target) : ''}

      ${renderPublicAchievements(target)}

      ${!full ? `
        <div class="reg__section">
          <div class="reg__section-title">${ICONS.upgrade} PRIVATE SECTIONS</div>
          <div class="reg__row small dim">
            Full wealth breakdowns, land + building counts, education, divorce
            records, and citizen locations are private. You can always see your
            own complete Registry sheet.
          </div>
        </div>
      ` : ''}
    </div>
    </div>
  `
}

function render(view, mode /* 'self' | 'public' */) {
  const p = getProfile()
  const k = active()
  const kid = activeId()
  const kingdom = KingdomConfig[kid]
  const tenureDays = p.joinedAt ? Math.floor((Date.now() - p.joinedAt) / 86400000) : 0
  const realEstate = getRealEstateValue() // CR-5: appreciating real estate (15%/yr)
  const netWorth = getNetWorth()           // cash + appreciated real estate
  const plotCounts = countPlotsByType(k)
  const buildingCounts = countBuildingsByType(k)
  const titleName = TitleConfig[p.title]?.name ?? '—'
  const className = ClassConfig[p.classId]?.name ?? '—'
  const cash = getWealth()
  const combined = combinedWealth(cash)
  const marriages = activeMarriages()
  const pastMarriages = (p.marriages ?? []).filter((m) => m.status === 'DIVORCED')
  const licenses = Object.keys(p.licenses ?? {}).filter((id) => p.licenses[id])
  const degrees = Object.entries(p.degrees ?? {})

  view.innerHTML = `
    <button class="reg__close" data-action="close" aria-label="Close registry">×</button>
    <div class="reg__scroll styled-scroll">
    <div class="reg__sheet">

      <div class="reg__header">
        <div class="reg__name">${escapeHtml(p.catName || '—')}</div>
        <div class="reg__sub">${className} · ${titleName} · ${kingdom?.name ?? kid}</div>
        <div class="reg__sub small">
          Citizen for <strong>${tenureDays}</strong> day${tenureDays === 1 ? '' : 's'}
          · ⚖ Honor <strong class="reg__honor">${getHonorDisplay()}</strong> / 10
          ${p.geo ? ` · ${escapeHtml(p.geo.label)}` : ''}
        </div>
        <div class="reg__header-actions">
          <button class="reg__icon-btn" data-action="open-cloud" title="Sync to Cloud" aria-label="Sync to cloud">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
            </svg>
          </button>
          <button class="reg__icon-btn" data-action="open-settings" title="Settings" aria-label="Settings">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
          <button class="reg__icon-btn" data-action="print" title="Print registry sheet" aria-label="Print">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
          </button>
          <button class="reg__icon-btn" data-action="share" title="Share / save registry" aria-label="Share">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/>
              <line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/>
            </svg>
          </button>
        </div>
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.wealth} WEALTH</div>
        <div class="reg__row hi"><span>Net worth</span><strong>${formatCoins(netWorth)}</strong></div>
        <div class="reg__row"><span>Cash on hand</span><strong>${formatCoins(cash)}</strong></div>
        <div class="reg__row"><span>Real estate (appreciating)</span><strong>${formatCoins(realEstate)}</strong></div>
        <div class="reg__row"><span>Tax rate</span><strong>${Math.round(getTaxRate() * 100)}%</strong></div>
        <div class="reg__row"><span>Bounties earned</span><strong>${formatCoins(getBountyTotal())}</strong></div>
        <div class="reg__row"><span>Lifetime clicks</span><strong>${k.clickCount ?? 0}</strong></div>
        <div class="reg__row"><span>Rival goons slain</span><strong>${getGoonsSlain()}</strong></div>
        ${
          combined > cash
            ? `<div class="reg__row hi"><span>Combined w/ spouse(s)</span><strong>${formatCoins(combined)}</strong></div>`
            : ''
        }
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.land} LAND (${k.plots ? Object.keys(k.plots).length : 0} owned)</div>
        ${Object.values(PlotTypes).map((t) =>
          `<div class="reg__row small"><span>${PlotConfig[t].name}</span><strong>${plotCounts[t] ?? 0}</strong></div>`
        ).join('')}
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.buildings} BUILDINGS</div>
        ${Object.keys(BuildingConfig).map((t) =>
          `<div class="reg__row small"><span>${BuildingConfig[t].name}</span><strong>${buildingCounts[t] ?? 0}</strong></div>`
        ).join('')}
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.citizens} CITIZENS</div>
        ${Object.values(GoonTypes).map((id) =>
          `<div class="reg__row small"><span>${GoonConfig[id].name}</span><strong>${getGoonsOwned(id)}</strong></div>`
        ).join('')}
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.education} EDUCATION</div>
        ${degrees.length
          ? degrees.map(([subject, tier]) =>
              `<div class="reg__row small"><span>${escapeHtml(DegreeSubjectLabels?.[subject] ?? subject)}</span><strong>${escapeHtml(DegreeTiers?.[tier]?.name ?? tier)}</strong></div>`
            ).join('')
          : `<div class="reg__row small dim">No degrees yet.</div>`
        }
        <div class="reg__section-title sub">LICENSES</div>
        ${licenses.length
          ? licenses.map((id) =>
              `<div class="reg__row small"><span>${escapeHtml(LicenseConfig?.[id]?.name ?? id)}</span><strong>✓</strong></div>`
            ).join('')
          : `<div class="reg__row small dim">No licenses yet.</div>`
        }
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.marriage} MARRIAGE</div>
        <div class="reg__row">
          <span>Status</span>
          <strong>${
            marriages.length
              ? `${marriages.length} active`
              : isOpenToMarriage()
                ? '<em>Open to marriage</em>'
                : 'Single'
          }</strong>
        </div>
        ${marriages.map((m) => {
          const blocked = birthBlockReason(m.id)
          return `
          <div class="reg__row small">
            <span>${m.role === 'requester' ? '&#9794;' : '&#9792;'} ${escapeHtml(m.partner?.name ?? 'Unknown')} (${escapeHtml(m.partner?.kingdom ?? '')})</span>
            <span class="reg__marriage-actions">
              <button class="ghost reg__btn-small" data-action="have-child" data-mid="${m.id}" ${blocked ? 'disabled title="' + escapeHtml(blocked) + '"' : ''}>+ child</button>
              <button class="reg__btn-danger" data-action="divorce" data-mid="${m.id}">Divorce</button>
            </span>
          </div>`
        }).join('')}
        <div class="reg__actions">
          <button data-action="toggle-open" class="ghost">${isOpenToMarriage() ? 'Hide from suitors' : 'Open to marriage'}</button>
          <button data-action="propose-mock" class="ghost">Test-propose to NPC</button>
        </div>
        <div class="reg__row small dim">
          Marriages with other players are arranged below and in the Registry.
        </div>
        ${pastMarriages.length
          ? `<div class="reg__section-title sub">${ICONS.divorce} DIVORCE RECORD</div>` +
            pastMarriages.map((m) => {
              const split = m.divorceSplit?.label ?? '—'
              const yours = m.settlement?.selfShare != null ? formatCoins(m.settlement.selfShare) : '—'
              return `<div class="reg__row small divorced">
                <span>${escapeHtml(m.partner?.name ?? 'Unknown')} (${escapeHtml(m.partner?.kingdom ?? '')}) · ${escapeHtml(split)}</span>
                <strong>${yours}</strong>
              </div>`
            }).join('')
          : ''
        }
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.marriage} MARRIAGE · LIVE PLAYERS</div>
        <div data-matchmaking>${liveMarriageHtml()}</div>
      </div>

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.children} CHILDREN${(p.children?.length ?? 0) ? ` (${p.children.length})` : ''}</div>
        ${(p.children?.length ?? 0)
          ? p.children.map((c) => {
              const ageMin = Math.max(0, Math.floor((Date.now() - (c.bornAt ?? Date.now())) / 60000))
              return `<div class="reg__row small">
                <span>&#x1F408; ${escapeHtml(c.name)} · child of ${escapeHtml(c.partner?.name ?? '—')}</span>
                <strong>${ageMin} min</strong>
              </div>`
            }).join('')
          : `<div class="reg__row small dim">No kittens yet. Add the <strong>Birth License</strong> + marry someone, then click "+ child" on an active marriage row.</div>`
        }
      </div>

      ${renderStatsSection(p)}

      ${renderAchievementsSection(p)}

      <div class="reg__section">
        <div class="reg__section-title">${ICONS.location} LOCATION</div>
        ${p.geo
          ? `<div class="reg__row"><span>City &amp; Country</span><strong>${escapeHtml(p.geo.label)}</strong></div>`
          : `<div class="reg__row small dim">Not shared. Only a city + country label, never coordinates.</div>
             <div class="reg__actions"><button data-action="share-location" class="ghost">Share my city &amp; country</button></div>`
        }
      </div>
    </div>
    </div>
  `

  // Divorce buttons are handled by the panel's root click listener via the
  // shared data-action="divorce" path (mid is read from the button itself).
}

function countPlotsByType(k) {
  const out = {}
  if (!k.plots) return out
  for (const tileIdx of Object.keys(k.plots)) {
    const t = k.plots[tileIdx]?.type
    if (t) out[t] = (out[t] ?? 0) + 1
  }
  return out
}

function countBuildingsByType(k) {
  const out = {}
  for (const b of k.buildings ?? []) out[b.type] = (out[b.type] ?? 0) + 1
  return out
}

function shareLocation() {
  if (!navigator.geolocation) {
    alert('Your browser does not support geolocation.')
    return
  }
  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      const { latitude, longitude } = pos.coords
      // Reverse-geocode to a city label via a free public endpoint. If the
      // request fails, fall back to a coarse "lat,lng" label so something
      // shows up — privacy-first: we never store the raw coordinates.
      const label = await reverseGeocode(latitude, longitude)
      const p = getProfile()
      p.geo = { label, sharedAt: Date.now() }
      emit(Events.ProfileChange, { profile: { ...p } })
    },
    (err) => alert(`Couldn't get location: ${err.message}`),
    { timeout: 8000 },
  )
}

async function reverseGeocode(lat, lng) {
  try {
    // OpenStreetMap Nominatim — free, no key required. Throttled to 1 req/s
    // per their TOS, but the Registry only calls this once per opt-in.
    // Mao Kingdom is a global game, so the displayed label always tries to
    // include the country alongside the local city/region.
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=10`
    const r = await fetch(url, { headers: { 'Accept': 'application/json' } })
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    const j = await r.json()
    const a = j.address ?? {}
    const city = a.city || a.town || a.village || a.hamlet || a.county || ''
    const region = a.state || a.region || ''
    const country = a.country || ''
    // Build "City, Region, Country", skipping any blank segments. Falls
    // back to just country if no city info is available.
    const parts = [city, region, country].filter(Boolean)
    // Dedupe — small cities/regions can share a name with their country.
    const seen = new Set()
    const unique = parts.filter((p) => {
      const k = p.toLowerCase()
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
    return unique.join(', ') || 'Unknown location'
  } catch {
    return 'Unknown location'
  }
}

// Unread-marriage-proposals indicator on the REGISTRY rail button. Tiny
// red dot that appears top-right when accountManager's polling detects a
// fresh incoming proposal. Cleared via markInboxSeen() when the player
// opens the registry. The dot is created once per panel-mount; subsequent
// paints just toggle visibility.
function installInboxDot(railBtn) {
  if (!railBtn || railBtn.querySelector('.reg__inbox-dot')) return
  railBtn.style.position = railBtn.style.position || 'relative'
  const dot = document.createElement('span')
  dot.className = 'reg__inbox-dot'
  dot.style.cssText = [
    'position:absolute', 'top:4px', 'right:4px',
    'width:9px', 'height:9px', 'border-radius:50%',
    'background:#ff3a3a', 'box-shadow:0 0 6px rgba(255,58,58,0.85), 0 0 0 1px rgba(0,0,0,0.5)',
    'pointer-events:none', 'display:none',
  ].join(';')
  railBtn.appendChild(dot)
  // Paint immediately in case proposals were already polled before mount.
  paintInboxDot(railBtn, getInboxUnread())
}

function paintInboxDot(railBtn, unread) {
  const dot = railBtn?.querySelector('.reg__inbox-dot')
  if (!dot) return
  dot.style.display = unread > 0 ? 'block' : 'none'
}

// The live-player marriage block: proposals to you, your marriages, and where to arrange more. It is drawn from inviteManager's state
// (kept fresh by its poll), so there is nothing to fetch here. Proposing / the full list lives on the Registry page (Household).
function liveMarriageHtml() {
  const acct = getAccountInfo()
  if (!acct.loggedIn) return `<div class="reg__row small dim">Log in to marry other players.</div>`
  const box = getInvites()
  if (!box.loaded) return `<div class="reg__row small dim">Loading…</div>`
  if (box.unavailable || !box.marriageSetup) return `<div class="reg__row small dim">Marriage between players is not switched on yet on this server.</div>`
  const R = box.marriageRules ?? { maxWives: 5 }
  const M = box.marriages, incoming = box.proposals.received.filter((p) => p.status === 'pending'), outgoing = box.proposals.sent.filter((p) => p.status === 'pending')
  const who = acct.gender === 'MALE' ? `&#9794; Male · ${M.wives.length} of ${R.maxWives} wives` : acct.gender === 'FEMALE' ? `&#9792; Female · ${M.husband ? 'married' : 'no husband yet'}` : 'No gender chosen yet'
  return `
    <div class="reg__row small"><span>${who}</span></div>
    ${incoming.length ? `<div class="reg__row small dim">Proposals to you:</div>` + incoming.map((p) => `<div class="reg__row small">
        <span>&#9829; ${escapeHtml(p.proposer.name)} (${escapeHtml(p.proposer.kingdom ?? '')}) · ${p.wifeId === acct.userId ? `his ${ord(p.wifeRank ?? 1)} wife` : `your ${ord(p.wifeRank ?? 1)} wife`}</span>
        <span class="reg__marriage-actions">
          <button class="ghost reg__btn-small" data-action="wed-accept" data-id="${escapeHtml(p.id)}">accept</button>
          <button class="reg__btn-danger" data-action="wed-decline" data-id="${escapeHtml(p.id)}">decline</button>
        </span></div>`).join('') : ''}
    ${M.wives.length ? `<div class="reg__row small dim">Your wives:</div>` + M.wives.map((m) => `<div class="reg__row small">
        <span>&#9792; ${escapeHtml(m.wife.name)} (${escapeHtml(m.wife.kingdom ?? '')})</span>
        <button class="reg__btn-danger" data-action="wed-end" data-id="${escapeHtml(m.id)}" data-name="${escapeHtml(m.wife.name)}">End</button></div>`).join('') : ''}
    ${M.husband ? `<div class="reg__row small dim">Your husband (he can see a report of your net worth and activity):</div><div class="reg__row small">
        <span>&#9794; ${escapeHtml(M.husband.husband.name)} (${escapeHtml(M.husband.husband.kingdom ?? '')})</span>
        <button class="reg__btn-danger" data-action="wed-end" data-id="${escapeHtml(M.husband.id)}" data-name="${escapeHtml(M.husband.husband.name)}">End</button></div>` : ''}
    ${outgoing.length ? `<div class="reg__row small dim">Awaiting reply: ${outgoing.map((p) => escapeHtml(p.targetName ?? 'a player')).join(', ')}</div>` : ''}
    <div class="reg__actions">
      ${M.wives.length ? '<button data-action="wed-report" class="ghost">Household report</button>' : ''}
      <button data-action="open-registry" class="ghost">Find a match in the Registry</button>
    </div>
    <div class="reg__row small dim">A male cat may have up to ${R.maxWives} wives, a female cat one husband. Both need a Marriage License.</div>`
}

async function liveMarriageAct(fn, okText) {
  try { await fn(); pushLog(okText) } catch (err) { pushLog(err?.body?.error ?? 'That did not work — try again.') }
}

// Divorce is consequential: combined wealth gets split per the Emperor's
// random decree and the record permanently appears on both parties'
// registries. Force the player to confirm with the emperor-mediator framing.
function confirmDivorce(marriageId) {
  const p = getProfile()
  const marriage = (p.marriages ?? []).find((m) => m.id === marriageId && m.status === 'ACTIVE')
  if (!marriage) return
  const partnerName = marriage.partner?.name ?? 'your spouse'

  const overlay = document.createElement('div')
  overlay.className = 'reg__confirm'
  overlay.innerHTML = `
    <div class="reg__confirm-card" role="dialog" aria-modal="true">
      <div class="reg__confirm-title">⚠ File for Divorce</div>
      <div class="reg__confirm-body">
        You are filing for divorce from <em>${escapeHtml(partnerName)}</em>.<br><br>
        The Emperor will be summoned as <strong>mediator</strong>. His decree on
        the wealth split is <strong>binding and random</strong> — by tradition the
        requester is favored, but the Emperor's mood on the day may grant an even
        split or anything in between.<br><br>
        Both parties will be notified. The settlement will be enforced
        immediately, and the divorce will appear permanently on both Registries.
      </div>
      <div class="reg__confirm-actions">
        <button class="cancel" data-confirm-cancel>Reconsider</button>
        <button class="confirm" data-confirm-yes>File the Decree</button>
      </div>
    </div>
  `
  document.body.appendChild(overlay)

  const close = () => overlay.remove()
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close() })
  overlay.querySelector('[data-confirm-cancel]').addEventListener('click', close)
  overlay.querySelector('[data-confirm-yes]').addEventListener('click', () => {
    close()
    const ok = marriageDivorce(marriageId)
    if (!ok) pushLog('Divorce decree could not be filed.')
  })
}

// Build a plain-text snapshot of the Registry and either pass it to the
// native Web Share sheet (mobile + modern desktop), or fall back to copying
// it to the clipboard. Saves the player a screenshot — they can paste the
// text into chat / a note / a screenshot caption.
async function shareRegistry() {
  const p = getProfile()
  const k = active()
  const kid = activeId()
  const lines = [
    `MAO KINGDOM — REGISTRY`,
    `Name: ${p.catName || '—'}`,
    `${ClassConfig[p.classId]?.name ?? '—'} · ${TitleConfig[p.title]?.name ?? '—'} · ${KingdomConfig[kid]?.name ?? kid}`,
    p.geo ? `Location: ${p.geo.label}` : null,
    ``,
    `Cash: ${formatCoins(getWealth())}`,
    `Bounties earned: ${formatCoins(getBountyTotal())}`,
    `Rival goons slain: ${getGoonsSlain()}`,
    `Lifetime clicks: ${k.clickCount ?? 0}`,
    `Land plots owned: ${k.plots ? Object.keys(k.plots).length : 0}`,
    `Buildings: ${(k.buildings ?? []).length}`,
    `Net worth: ${formatCoins(getNetWorth())}`,
    `Honor: ${getHonorDisplay()} / 10`,
    `Active marriages: ${activeMarriages().length}`,
  ].filter(Boolean).join('\n')

  const shareData = { title: 'Mao Kingdom Registry', text: lines }
  try {
    if (navigator.share) {
      await navigator.share(shareData)
      return
    }
  } catch {
    /* user cancelled — fall through to clipboard */
  }
  try {
    await navigator.clipboard.writeText(lines)
    pushLog('Registry copied to clipboard.')
  } catch {
    alert(lines)
  }
}

// Inline SVG icons rendered next to each Registry section title. Keeping
// them as raw SVG strings (instead of a sprite sheet) means no extra build
// step or asset pipeline — just inject them into the existing markup.
const ICONS = {
  membership: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15 8.5 22 9.3 17 14 18.2 21 12 17.8 5.8 21 7 14 2 9.3 9 8.5 12 2"/></svg>`,
  wealth: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v10M9 10h6M9 14h6"/></svg>`,
  land: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 3 22 12 12 21 2 12 12 3"/></svg>`,
  buildings: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="10" width="7" height="11"/><rect x="14" y="6" width="7" height="15"/><path d="M3 10l3.5-3 3.5 3"/></svg>`,
  citizens: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="8" r="3"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><circle cx="17" cy="10" r="2.5"/><path d="M15 21v-1a3 3 0 0 1 3-3h.5"/></svg>`,
  education: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10L12 5 2 10l10 5 10-5z"/><path d="M6 12v5c0 2 3 4 6 4s6-2 6-4v-5"/></svg>`,
  marriage: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8.5" cy="11" r="4"/><circle cx="15.5" cy="13" r="4"/></svg>`,
  divorce: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8.5" cy="11" r="4"/><circle cx="15.5" cy="13" r="4"/><line x1="2" y1="22" x2="22" y2="2"/></svg>`,
  location: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s-7-8-7-13a7 7 0 0 1 14 0c0 5-7 13-7 13z"/><circle cx="12" cy="9" r="2.5"/></svg>`,
  upgrade: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>`,
  children: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="9" r="2.5"/><circle cx="18" cy="9" r="2.5"/><circle cx="12" cy="15" r="2"/><path d="M4 19c0-1.5 1.5-3 2-3M20 19c0-1.5-1.5-3-2-3M10 21c0-1 1-2 2-2s2 1 2 2"/></svg>`,
  trophy: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 4h8v4a4 4 0 0 1-8 0V4z"/><path d="M5 4h3v3a3 3 0 1 1-3-3zM16 4h3a3 3 0 1 1-3 3V4z"/><path d="M9 14h6v2H9zM8 20h8M10 18v2M14 18v2"/></svg>`,
}

// Friendly labels for each tracked counter, in display order. Counters
// not in this list won't be shown (Click and PlotChanged are intentionally
// hidden — Click duplicates the kingdom-level clickCount and PlotChanged
// also fires for damage events so it'd be misleading).
const STAT_ROWS = [
  { key: 'WildGoonSlain',     icon: '&#x2694;',  label: 'Goons slain' },
  { key: 'WildGoonConverted', icon: '&#x262F;',  label: 'Goons converted' },
  { key: 'BuildingAdded',     icon: '&#x1F3D7;', label: 'Buildings erected' },
  { key: 'FeedActivated',     icon: '&#x1F963;', label: 'Times fed citizens' },
  { key: 'RaidEnd',           icon: '&#x1F525;', label: 'Raids survived' },
  { key: 'MarriageAccepted',  icon: '&#x1F48D;', label: 'Marriages' },
  { key: 'MarriageDivorced',  icon: '&#x1F494;', label: 'Divorces' },
  { key: 'ChildBorn',         icon: '&#x1F408;', label: 'Kittens born' },
  { key: 'AdventureCompleted',icon: '&#x1F30C;', label: 'Adventures completed' },
]

function renderStatsSection(p) {
  const counters = p.counters ?? {}
  const streak = getStreak()
  // Render a row for every tracked stat — even zeros, so the player sees
  // what's being measured. Lifetime tally is across kingdoms.
  const rows = STAT_ROWS.map((s) => `<div class="reg__row small">
    <span>${s.icon} ${s.label}</span>
    <strong>${(counters[s.key] ?? 0).toLocaleString()}</strong>
  </div>`).join('')
  // Login-streak row only shows when there's something to show (registered
  // accounts after their first /me hit). Both current + longest in one row.
  const streakRow = streak.current > 0 ? `<div class="reg__row small">
    <span>&#x1F525; Login streak</span>
    <strong>${streak.current} day${streak.current === 1 ? '' : 's'}${streak.longest > streak.current ? ` <span class="reg__row-aux">· best ${streak.longest}</span>` : ''}</strong>
  </div>` : ''
  return `<div class="reg__section">
    <div class="reg__section-title">${ICONS.trophy} LIFETIME STATS</div>
    ${streakRow}
    ${rows}
  </div>`
}

// Public lifetime-counters block for KC viewers inspecting another player.
// Mirrors the self-view STATS section so the experience is consistent.
function renderPublicStatsSection(counters, target) {
  const rows = STAT_ROWS.map((s) => `<div class="reg__row small">
    <span>${s.icon} ${s.label}</span>
    <strong>${(counters?.[s.key] ?? 0).toLocaleString()}</strong>
  </div>`).join('')
  const cur = target?.currentStreak ?? 0
  const longest = target?.longestStreak ?? 0
  const streakRow = (cur > 0 || longest > 0) ? `<div class="reg__row small">
    <span>&#x1F525; Login streak</span>
    <strong>${cur} day${cur === 1 ? '' : 's'}${longest > cur ? ` <span class="reg__row-aux">· best ${longest}</span>` : ''}</strong>
  </div>` : ''
  return `<div class="reg__section">
    <div class="reg__section-title">${ICONS.trophy} LIFETIME STATS</div>
    ${streakRow}
    ${rows}
  </div>`
}

// Public, badges-only view of another player's earned achievements. Shows
// no locked rows (no spoilers); FREE-tier viewers see just the count.
function renderPublicAchievements(target) {
  const earnedIds = Object.keys(target?.achievements ?? {})
  const total = AchievementConfig.length
  if (!earnedIds.length) {
    return `<div class="reg__section">
      <div class="reg__section-title">${ICONS.trophy} ACHIEVEMENTS (0/${total})</div>
      <div class="reg__row small dim">No badges earned yet.</div>
    </div>`
  }
  const badges = earnedIds.map((id) => {
    const def = AchievementConfig.find((d) => d.id === id)
    if (!def) return ''
    const r = AchievementRarity[def.rarity] ?? AchievementRarity.common
    return `<span class="reg__ach-chip" style="border-color:${r.color}" title="${escapeHtml(def.desc)}">
      <span class="reg__ach-badge" style="background:${r.color}">${r.label}</span>
      ${escapeHtml(def.name)}
    </span>`
  }).join('')
  return `<div class="reg__section">
    <div class="reg__section-title">${ICONS.trophy} ACHIEVEMENTS (${earnedIds.length}/${total})</div>
    <div class="reg__ach-chips">${badges}</div>
  </div>`
}

// Render the ACHIEVEMENTS panel. Earned achievements are shown big, in
// reverse chronological order; locked achievements list at the bottom as
// dim hints (no spoilery numbers — just name + flavor).
function renderAchievementsSection(p) {
  const earned = []
  const locked = []
  const counters = p.counters ?? {}
  const achievements = p.achievements ?? {}
  for (const def of AchievementConfig) {
    if (achievements[def.id]) {
      earned.push({ def, at: achievements[def.id].awardedAt })
    } else {
      locked.push(def)
    }
  }
  earned.sort((a, b) => (b.at ?? 0) - (a.at ?? 0))
  const total = AchievementConfig.length
  const card = ({ def, at }) => {
    const r = AchievementRarity[def.rarity] ?? AchievementRarity.common
    const when = at ? new Date(at).toLocaleDateString() : ''
    return `<div class="reg__ach earned" style="border-color:${r.color}">
      <div class="reg__ach-row">
        <span class="reg__ach-badge" style="background:${r.color}">${r.label}</span>
        <strong>${escapeHtml(def.name)}</strong>
        ${when ? `<span class="reg__ach-when">${escapeHtml(when)}</span>` : ''}
      </div>
      <div class="reg__ach-desc">${escapeHtml(def.desc)}</div>
    </div>`
  }
  const lockedCard = (def) => {
    const r = AchievementRarity[def.rarity] ?? AchievementRarity.common
    const prog = progressFor(def)
    // Bar only renders when the trigger has measurable progress. Profile
    // flag / membership tier / server-first triggers have no in-progress
    // signal — they just stay as plain locked rows.
    const bar = prog ? `<div class="reg__ach-prog">
      <div class="reg__ach-prog-bar"><div class="reg__ach-prog-fill" style="width:${Math.round(100 * prog.current / prog.target)}%;background:${r.color}"></div></div>
      <span class="reg__ach-prog-label">${prog.current.toLocaleString()} / ${prog.target.toLocaleString()}</span>
    </div>` : ''
    return `<div class="reg__ach locked">
      <div class="reg__ach-row dim">
        <span class="reg__ach-badge" style="background:${r.color};opacity:.4">${r.label}</span>
        <strong>${escapeHtml(def.name)}</strong>
      </div>
      <div class="reg__ach-desc dim">${escapeHtml(def.desc)}</div>
      ${bar}
    </div>`
  }
  return `<div class="reg__section">
    <div class="reg__section-title">${ICONS.trophy} ACHIEVEMENTS (${earned.length}/${total})</div>
    ${earned.length
      ? earned.map(card).join('')
      : `<div class="reg__row small dim">No badges yet. Start by tending the field and slaying a goon.</div>`}
    ${locked.length
      ? `<div class="reg__section-title sub">LOCKED</div>${locked.map(lockedCard).join('')}`
      : ''}
  </div>`
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]))
}
